import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { CommissionType, CreateCommissionTypeRequest, UpdateCommissionTypeRequest } from "@/types/commission";
import { toast } from "sonner";

export const useCommissionTypes = () => {
  const [commissionTypes, setCommissionTypes] = useState<CommissionType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Obtener todos los tipos de comisión
  const fetchCommissionTypes = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('commission_types')
        .select('*')
        .order('name', { ascending: true });

      if (fetchError) {
        console.error("Error al obtener tipos de comisión:", fetchError);
        throw fetchError;
      }

      setCommissionTypes(data || []);
    } catch (err) {
      console.error("Error al cargar tipos de comisión:", err);
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(`Error al cargar tipos de comisión: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  // Actualizar un tipo de comisión
  const updateCommissionType = async (id: string, updates: UpdateCommissionTypeRequest) => {
    try {
      const { data, error: updateError } = await supabase
        .from('commission_types')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (updateError) {
        console.error("Error al actualizar tipo de comisión:", updateError);
        throw updateError;
      }

      // Actualizar la lista local
      setCommissionTypes(prev => 
        prev.map(type => type.id === id ? { ...type, ...data } : type)
      );

      toast.success("Tipo de comisión actualizado exitosamente");
      return data;
    } catch (err) {
      console.error("Error al actualizar tipo de comisión:", err);
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      toast.error(`Error al actualizar: ${errorMessage}`);
      throw err;
    }
  };

  // Crear un nuevo tipo de comisión
  const createCommissionType = async (commissionType: CreateCommissionTypeRequest) => {
    try {
      const { data, error: createError } = await supabase
        .from('commission_types')
        .insert(commissionType)
        .select()
        .single();

      if (createError) {
        console.error("Error al crear tipo de comisión:", createError);
        throw createError;
      }

      // Agregar a la lista local
      setCommissionTypes(prev => [...prev, data]);

      toast.success("Tipo de comisión creado exitosamente");
      return data;
    } catch (err) {
      console.error("Error al crear tipo de comisión:", err);
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      toast.error(`Error al crear: ${errorMessage}`);
      throw err;
    }
  };

  // Eliminar un tipo de comisión
  const deleteCommissionType = async (id: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('commission_types')
        .delete()
        .eq('id', id);

      if (deleteError) {
        console.error("Error al eliminar tipo de comisión:", deleteError);
        throw deleteError;
      }

      // Remover de la lista local
      setCommissionTypes(prev => prev.filter(type => type.id !== id));

      toast.success("Tipo de comisión eliminado exitosamente");
    } catch (err) {
      console.error("Error al eliminar tipo de comisión:", err);
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      toast.error(`Error al eliminar: ${errorMessage}`);
      throw err;
    }
  };

  // Cargar datos al montar el componente
  useEffect(() => {
    fetchCommissionTypes();
  }, []);

  return {
    commissionTypes,
    loading,
    error,
    fetchCommissionTypes,
    updateCommissionType,
    createCommissionType,
    deleteCommissionType
  };
}; 